import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminTableLoadingOverlay from '../../components/admin/AdminTableLoadingOverlay';
import { useDebounce } from '../../hooks/useDebounce';

const FETCH_LIMIT = 500;

const AdminUsers = () => {
  const [rawUsers, setRawUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    fetchUsers();
  }, [debouncedSearch]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getUsers({
        search: debouncedSearch || undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      setRawUsers(response.data.users);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = statusFilter
    ? rawUsers.filter((u) => (statusFilter === 'active' ? u.is_active : !u.is_active))
    : rawUsers;
  const total = filteredUsers.length;
  const users = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  const handleAction = async (userId, action) => {
    try {
      if (action === 'suspend') {
        await adminAPI.suspendUser(userId);
        toast.success('User suspended');
      } else {
        await adminAPI.activateUser(userId);
        toast.success('User activated');
      }
      fetchUsers();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const filterOptions = [
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
  ];

  if (loading && users.length === 0) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-users">
      <h1 className="text-2xl sm:text-4xl font-bold text-foreground">User Management</h1>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name or email..."
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); setPage(1); }}
        filterOptions={filterOptions}
        filterPlaceholder="All statuses"
        total={total}
        loading={loading}
      />

      <Card className="glass-card p-6 overflow-hidden">
        <AdminTableLoadingOverlay loading={loading}>
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Name</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Email</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Role</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Status</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Joined</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="table-row" data-testid={`user-row-${user.id}`}>
                  <td className="py-4 text-foreground">{user.full_name}</td>
                  <td className="py-4 text-foreground">{user.email}</td>
                  <td className="py-4 text-foreground capitalize">{user.role}</td>
                  <td className="py-4">
                    <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {user.is_active ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td className="py-4 text-muted-foreground text-sm">{formatDate(user.created_at)}</td>
                  <td className="py-4">
                    <Button
                      size="sm"
                      onClick={() => handleAction(user.id, user.is_active ? 'suspend' : 'activate')}
                      variant={user.is_active ? 'destructive' : 'default'}
                      data-testid={`action-btn-${user.id}`}
                    >
                      {user.is_active ? 'Suspend' : 'Activate'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} total={total} onPageChange={setPage} loading={loading} />
        </AdminTableLoadingOverlay>
      </Card>
    </div>
  );
};

export default AdminUsers;
